/**
 * Documenting and correcting an allergy (DECISIONS.md #46).
 *
 * **The panel that was read-only until now.** `PHASE-0.md` §2.4 asked for allergies to be displayed;
 * what an outage actually produces is a patient whose penicillin allergy nobody has been able to file
 * since the central system went down, and a panel that can only display is a panel that goes stale
 * for the length of the outage.
 *
 * **Nothing is replaced.** A correction is a new version and the one on screen stays in the record
 * (AGENTS.md §3 rule 2), which the form says above the button rather than in a dialogue that gets
 * clicked through — the same choice `PatientCorrectionScreen` makes about being final.
 *
 * **There is no draft module, unlike the registration and the patient correction.** Every rule here
 * is about record content the server owns — which statuses exist, whether a substance belongs with
 * this one, how long a field may be — and `AllergyContent.Validate` already refuses each with its own
 * code and its own sentence. A second copy of those rules in TypeScript would be a second thing to
 * keep in step, and the failure mode of drift is a form that accepts what the record then refuses, or
 * refuses what the record would have taken.
 *
 * **Nothing is saved until Save** (#38): there is no draft table and no `localStorage`, so the form
 * warns before discarding and before the tab closes.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AllergyRefusedError,
  AllergyUnavailableError,
  correctAllergy,
  documentAllergy,
  type AllergyRefusalCode,
  type AllergyRefusalParams,
  type AllergySubmission,
  type WrittenAllergy,
} from './allergyApi'
import type { PatientAllergy } from './patientOverviewApi'
import {
  allergyCriticalityLabel,
  allergyRefusalMessage,
  allergySeverityLabel,
  allergyStatusLabel,
  allergyVerificationLabel,
  notAnsweredLabel,
  safetyUnavailableMessage,
} from './safetyDisplay'

/**
 * The statuses the form offers, which depends on whether there is a version to supersede.
 *
 * `entered-in-error` only on a correction: writing a first version in order to say it is wrong
 * documents nothing, and the server refuses it (`ALLERGY_STATUS_NOT_ALLOWED`).
 */
function offeredStatuses(correcting: boolean): readonly string[] {
  return correcting
    ? ['active', 'resolved', 'no-known-allergies', 'entered-in-error']
    : ['active', 'resolved', 'no-known-allergies']
}

const OFFERED_SEVERITIES = ['mild', 'moderate', 'severe'] as const
const OFFERED_CRITICALITIES = ['low', 'high', 'unable-to-assess'] as const
const OFFERED_VERIFICATIONS = ['confirmed', 'unconfirmed'] as const

/**
 * The longest a substance or a reaction may be, mirroring `AllergyContent`.
 *
 * A `maxLength` on the box rather than a check: stopping the keystroke is kinder than refusing the
 * save, and the server still refuses anything that gets past it (`ALLERGY_FIELD_TOO_LONG`), which is
 * what keeps this number from being load-bearing.
 */
const MAXIMUM_FIELD_LENGTH = 200

/** The form's fields, as text. Empty string means the column is null. */
interface AllergyDraft {
  readonly status: string
  readonly substance: string
  readonly reaction: string
  readonly severity: string
  readonly criticality: string
  readonly verification: string
}

/**
 * The draft a correction opens with, or an empty one for a first version.
 *
 * **Derived from the prop on every render, never copied on mount.** That is what makes the stale path
 * work: after a refusal the overview refetches, this arrives holding what the record now says, and
 * the unsaved-work guard compares against that rather than against what was current when the form
 * opened.
 */
function draftFrom(allergy: PatientAllergy | null): AllergyDraft {
  return {
    status: allergy?.status ?? 'active',
    substance: allergy?.substance ?? '',
    reaction: allergy?.reaction ?? '',
    severity: allergy?.severity ?? '',
    criticality: allergy?.criticality ?? '',
    verification: allergy?.verification ?? '',
  }
}

function isDirty(draft: AllergyDraft, original: AllergyDraft): boolean {
  return (
    draft.status !== original.status ||
    draft.substance !== original.substance ||
    draft.reaction !== original.reaction ||
    draft.severity !== original.severity ||
    draft.criticality !== original.criticality ||
    draft.verification !== original.verification
  )
}

/** Trimmed, or null for a column that should be empty. */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function toSubmission(draft: AllergyDraft, supersedes: string | null): AllergySubmission {
  return {
    supersedes,
    status: draft.status,
    // Null when the status names no substance, whatever is still in the box. The field is hidden in
    // that state, so this is about the value surviving a status change rather than about trusting the
    // DOM — and sending both is a refusal (`ALLERGY_SUBSTANCE_NOT_ALLOWED`) rather than a silent win.
    substance: draft.status === 'no-known-allergies' ? null : orNull(draft.substance),
    reaction: orNull(draft.reaction),
    severity: orNull(draft.severity),
    criticality: orNull(draft.criticality),
    verification: orNull(draft.verification),
  }
}

type Phase =
  | { readonly kind: 'editing' }
  /** A write is in flight. The form is read-only and Save cannot be pressed twice. */
  | { readonly kind: 'saving' }
  /** Written. The form is gone, so the same allergy cannot be documented a second time. */
  | { readonly kind: 'written' }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: AllergyRefusalCode | 'UNKNOWN'
      readonly params: AllergyRefusalParams
    }
  | { readonly kind: 'unavailable' }

/**
 * The allergy form, for a first version or for a correction.
 *
 * The registration form's classes throughout, deliberately: it is the same kind of form, and a second
 * set of styles for it would be a second thing to keep in step.
 *
 * @param patientId Whose record this is written to.
 * @param allergy The allergy being corrected, or null to document one that is not in the record.
 *   **Read on every render rather than copied on mount**, which is what lets a stale refusal be
 *   followed by a refetch and then by a Save against the version that is now current.
 * @param onWritten The row was written. The overview refetches, so the panel shows what the record
 *   now holds while the confirmation is still up.
 * @param onStale Somebody else corrected this allergy. Refetches without blanking the screen; the
 *   form stays mounted and keeps the draft.
 * @param onClose Leaves the form.
 */
export function AllergyForm({
  patientId,
  allergy,
  onWritten,
  onStale,
  onClose,
}: {
  readonly patientId: string
  readonly allergy: PatientAllergy | null
  readonly onWritten: (written: WrittenAllergy) => void
  readonly onStale: () => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'patient', 'safety'])

  const correcting = allergy !== null

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [draft, setDraft] = useState<AllergyDraft>(() => draftFrom(allergy))
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /**
   * Whether a `setState` would still be seen.
   *
   * A submit in flight is deliberately **not** aborted on unmount — see `documentAllergy`. A write
   * that reached the server happened, and cancelling the request would not undo it, it would only
   * hide the answer.
   */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const original = draftFrom(allergy)
  const dirty = isDirty(draft, original)
  const busy = phase.kind === 'saving'

  /**
   * The browser's own "leave this page?" question, while there is something unsaved.
   *
   * The only guard against a reload or a closed tab, and it is here because #38 means there is
   * nothing to come back to. The wording is the browser's and cannot be set, which is why the
   * on-screen sentence says it too.
   */
  useEffect(() => {
    if (!dirty || phase.kind === 'written') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()

      // Both, for the reason `NoteEditor` gives: `preventDefault()` alone is the current spec and
      // only Chrome 119 and later honour it, while Firefox and Safari cancel on a non-empty
      // `returnValue`. The value is never displayed, so it is not a catalogue key.
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const update = <Field extends keyof AllergyDraft>(field: Field, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value }))

    // A refusal describes a form that has since changed. Keeping it on screen while the clinician
    // fixes it would leave them reading an error about something they have already corrected.
    setNotice(null)
  }

  const submit = () => {
    if (busy) {
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const controller = new AbortController()
    const submission = toSubmission(draft, allergy?.versionId ?? null)

    // `allergy.versionId` read here and not at mount: it is the version this Save is against, and
    // after a stale refusal and a refetch it is a different one.
    const written = correcting
      ? correctAllergy(patientId, allergy.allergyId, submission, controller.signal)
      : documentAllergy(patientId, submission, controller.signal)

    written.then(
      (result) => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'written' })
        onWritten(result)
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        // Back to editing with every field untouched, whatever went wrong. The form is the only copy
        // of what the clinician established.
        setPhase({ kind: 'editing' })

        if (cause instanceof AllergyRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          if (cause.code === 'ALLERGY_VERSION_STALE') {
            // The one refusal the screen acts on by itself: reload the patient so the next Save names
            // the version that is current. The draft is untouched, so what was typed is still there
            // to compare against what the other clinician wrote.
            onStale()
          }
        } else if (cause instanceof AllergyUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const heading = correcting
    ? t('safety:allergy.headingCorrect')
    : t('safety:allergy.headingDocument')

  if (phase.kind === 'written') {
    return (
      <section className="registration patient-correction" aria-labelledby="allergy-form-heading">
        <h3 id="allergy-form-heading">{heading}</h3>

        {/* `status`, not `alert`: it is good news, and it must not interrupt. */}
        <p className="registration__registered" role="status">
          {correcting ? t('safety:allergy.savedCorrect') : t('safety:allergy.savedDocument')}
        </p>

        <button type="button" onClick={onClose}>
          {t('safety:form.close')}
        </button>
      </section>
    )
  }

  const namesSubstance = draft.status !== 'no-known-allergies'

  return (
    <section className="registration patient-correction" aria-labelledby="allergy-form-heading">
      <header className="registration__header">
        <h3 id="allergy-form-heading">{heading}</h3>

        <p className="registration__explains">
          {correcting
            ? t('safety:allergy.explanationCorrect')
            : t('safety:allergy.explanationDocument')}
        </p>
      </header>

      <div className="registration__field">
        <label htmlFor="allergy-field-status">{t('safety:allergy.fieldStatus')}</label>

        <p id="allergy-field-status-hint" className="registration__hint">
          {t('safety:allergy.hintStatus')}
        </p>

        <select
          id="allergy-field-status"
          value={draft.status}
          disabled={busy}
          aria-describedby="allergy-field-status-hint"
          onChange={(event) => update('status', event.target.value)}
        >
          {offeredStatuses(correcting).map((status) => (
            <option key={status} value={status}>
              {allergyStatusLabel(status, t)}
            </option>
          ))}
        </select>
      </div>

      {/* Hidden rather than disabled when the status names no substance: a greyed-out box invites a
          clinician to wonder what belongs in it, and the answer here is nothing. The draft keeps the
          text, so changing the status back brings it into view unchanged. */}
      {namesSubstance && (
        <>
          <div className="registration__field">
            <label htmlFor="allergy-field-substance">{t('safety:allergy.fieldSubstance')}</label>

            <p id="allergy-field-substance-hint" className="registration__hint">
              {t('safety:allergy.hintSubstance')}
            </p>

            <input
              id="allergy-field-substance"
              type="text"
              value={draft.substance}
              disabled={busy}
              // The cursor starts on the substance, which is the field this form exists for.
              autoFocus
              aria-describedby="allergy-field-substance-hint"
              maxLength={MAXIMUM_FIELD_LENGTH}
              onChange={(event) => update('substance', event.target.value)}
            />
          </div>

          <div className="registration__field">
            <label htmlFor="allergy-field-reaction">
              {t('safety:allergy.fieldReaction')}
              <span className="registration__optional"> ({t('safety:form.optional')})</span>
            </label>

            <p id="allergy-field-reaction-hint" className="registration__hint">
              {t('safety:allergy.hintReaction')}
            </p>

            <input
              id="allergy-field-reaction"
              type="text"
              value={draft.reaction}
              disabled={busy}
              aria-describedby="allergy-field-reaction-hint"
              maxLength={MAXIMUM_FIELD_LENGTH}
              onChange={(event) => update('reaction', event.target.value)}
            />
          </div>

          <VocabularyField
            field="severity"
            label={t('safety:allergy.fieldSeverity')}
            hint={t('safety:allergy.hintSeverity')}
            value={draft.severity}
            options={OFFERED_SEVERITIES}
            optionLabel={(value) => allergySeverityLabel(value, t)}
            disabled={busy}
            notAnswered={notAnsweredLabel(t)}
            optionalMarker={t('safety:form.optional')}
            onChange={(value) => update('severity', value)}
          />

          <VocabularyField
            field="criticality"
            label={t('safety:allergy.fieldCriticality')}
            hint={t('safety:allergy.hintCriticality')}
            value={draft.criticality}
            options={OFFERED_CRITICALITIES}
            optionLabel={(value) => allergyCriticalityLabel(value, t)}
            disabled={busy}
            notAnswered={notAnsweredLabel(t)}
            optionalMarker={t('safety:form.optional')}
            onChange={(value) => update('criticality', value)}
          />

          <VocabularyField
            field="verification"
            label={t('safety:allergy.fieldVerification')}
            hint={t('safety:allergy.hintVerification')}
            value={draft.verification}
            options={OFFERED_VERIFICATIONS}
            optionLabel={(value) => allergyVerificationLabel(value, t)}
            disabled={busy}
            notAnswered={notAnsweredLabel(t)}
            optionalMarker={t('safety:form.optional')}
            onChange={(value) => update('verification', value)}
          />
        </>
      )}

      {/* Announced, because a refusal is the whole message on this form and the clinician's attention
          is on what they have just established about the patient. */}
      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'unavailable'
                ? safetyUnavailableMessage(t)
                : allergyRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        {/* The one thing about writing here that a clinician needs to know once, said where the
            decision is made. Not a warning: that both versions stay is the design working. */}
        <p className="registration__final">{t('safety:form.appendOnly')}</p>

        <button
          type="button"
          className="registration__register"
          // Disabled only while a write is in flight — a second press would write a second row, and
          // there is no `DELETE` (AGENTS.md §3). Never disabled for a problem in the form: a
          // greyed-out button with no explanation is the worse failure in a stressed setting, and
          // every rule here belongs to the server anyway.
          disabled={busy}
          onClick={submit}
        >
          {busy ? t('safety:form.saving') : t('safety:form.save')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('safety:form.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('safety:form.discardQuestion')}
        >
          <p>{t('safety:form.discardQuestion')}</p>

          {/* Keep filling in is first and is the safe one. The destructive button says what it does
              rather than "OK" — this is the click that loses what was typed. */}
          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('safety:form.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('safety:form.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * A select over a closed vocabulary, with an empty option that means nobody answered.
 *
 * **The empty option is not one of the values**, and the hint under each field says so. A severity
 * left empty is a severity nobody established, and reading it as `mild` is exactly the wrong way to
 * be wrong about an allergy — which is why the record keeps the two apart and the form must too.
 *
 * @param field Names the control, for the label's `htmlFor`.
 * @param label The field's heading.
 * @param hint The sentence under it, which is where "empty is not mild" is actually said.
 * @param value The code currently chosen, or the empty string.
 * @param options The codes offered, in the order they are offered.
 * @param optionLabel Turns a code into words.
 * @param disabled Whether a write is in flight.
 * @param notAnswered The wording of the empty option.
 * @param optionalMarker The word beside the label for a field that may be left empty.
 * @param onChange Receives the chosen code, or the empty string.
 */
function VocabularyField({
  field,
  label,
  hint,
  value,
  options,
  optionLabel,
  disabled,
  notAnswered,
  optionalMarker,
  onChange,
}: {
  readonly field: string
  readonly label: string
  readonly hint: string
  readonly value: string
  readonly options: readonly string[]
  readonly optionLabel: (value: string) => string
  readonly disabled: boolean
  readonly notAnswered: string
  readonly optionalMarker: string
  readonly onChange: (value: string) => void
}) {
  const id = `allergy-field-${field}`
  const hintId = `${id}-hint`

  return (
    <div className="registration__field">
      <label htmlFor={id}>
        {label}
        <span className="registration__optional"> ({optionalMarker})</span>
      </label>

      <p id={hintId} className="registration__hint">
        {hint}
      </p>

      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-describedby={hintId}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{notAnswered}</option>

        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    </div>
  )
}
